import React, { useState, useEffect } from 'react';
import { Plus, Calendar, User, Clock, CheckCircle, XCircle, Phone, Mail, MessageCircle, Users, MoreVertical, CalendarCheck, MapPin, Video } from 'lucide-react';
import type { SalesActivity, Client } from '../../lib/types';
import { useApp } from '../AppProvider';
import { useSettingsStore } from '../../lib/stores/settingsStore';
import { useMenuLabels } from '../../lib/hooks/useMenuLabels';
import { useActivityTypesStore } from '../../lib/stores/activityTypesStore';
import { ActivityTypeIcon } from '../settings/CustomActivityTypes';

interface LeadActivitiesSectionProps {
  lead: Client;
  activities: SalesActivity[];
  onActivityCreate?: (activity: Partial<SalesActivity>) => void;
  onActivityUpdate?: (activityId: string, updates: Partial<SalesActivity>) => void;
  onActivityDelete?: (activityId: string) => void;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
}

export function LeadActivitiesSection({
  lead,
  activities,
  onActivityCreate,
  onActivityUpdate,
  onActivityDelete,
  canCreate = true,
  canUpdate = true,
  canDelete = true
}: LeadActivitiesSectionProps) {
  const { toast } = useApp();
  const { settings } = useSettingsStore();
  const { lead: leadLabel } = useMenuLabels();

  // Tipi attività custom (per-org) — mostrati in coda alle opzioni di sistema
  const { customTypes, fetched: customTypesFetched, fetchCustomTypes } = useActivityTypesStore();
  useEffect(() => {
    if (!customTypesFetched) fetchCustomTypes();
  }, [customTypesFetched, fetchCustomTypes]);
  const activeCustomTypes = customTypes.filter(t => t.active);

  // Activity settings with retrocompatibility fallback
  const activityDefaultType = settings?.activity_default_type || 'task';
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingActivity, setEditingActivity] = useState<string | null>(null);
  const [newActivity, setNewActivity] = useState({
    title: '',
    description: '',
    type: (() => {
      const dbValidTypes = ['call', 'videochiamata', 'appuntamento', 'sopralluogo', 'email', 'task', 'note', 'other'];
      const effectiveDefault = activityDefaultType === 'meeting' ? 'videochiamata' : activityDefaultType;
      return (dbValidTypes.includes(effectiveDefault) ? effectiveDefault : 'task');
    })() as 'call' | 'videochiamata' | 'email' | 'task' | 'appuntamento' | 'sopralluogo' | 'other',
    due_date: '',
    end_date: '',
    status: 'pending' as 'pending' | 'completed' | 'overdue'
  });

  // Funzione per calcolare end_date come due_date + 15 minuti
  const calculateEndDate = (startDateTime: string): string => {
    if (!startDateTime) return '';
    const start = new Date(startDateTime);
    start.setMinutes(start.getMinutes() + 15);
    // Formato per datetime-local: YYYY-MM-DDTHH:MM
    const year = start.getFullYear();
    const month = String(start.getMonth() + 1).padStart(2, '0');
    const day = String(start.getDate()).padStart(2, '0');
    const hours = String(start.getHours()).padStart(2, '0');
    const minutes = String(start.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  // Aggiorna il tipo quando le settings cambiano
  useEffect(() => {
    if (settings) {
      const dbValidTypes = ['call', 'videochiamata', 'appuntamento', 'sopralluogo', 'email', 'task', 'note', 'other'];
      const defaultType = settings.activity_default_type || 'task';
      const effectiveDefault = defaultType === 'meeting' ? 'videochiamata' : defaultType;
      const safeDefault = dbValidTypes.includes(effectiveDefault) ? effectiveDefault : 'task';
      setNewActivity(prev => ({ ...prev, type: safeDefault as any }));
    }
  }, [settings]);

  // Activity type icons and colors
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call':
        return <Phone className="h-4 w-4" />;
      case 'videochiamata':
        return <Video className="h-4 w-4" />;
      case 'email':
        return <Mail className="h-4 w-4" />;
      case 'task':
        return <CheckCircle className="h-4 w-4" />;
      case 'appuntamento':
        return <CalendarCheck className="h-4 w-4" />;
      case 'sopralluogo':
        return <MapPin className="h-4 w-4" />;
      default: {
        const custom = customTypes.find(t => t.slug === type);
        if (custom) return <ActivityTypeIcon icon={custom.icon} className="h-4 w-4" />;
        return <MessageCircle className="h-4 w-4" />;
      }
    }
  };

  const getActivityColor = (type: string) => {
    switch (type) {
      case 'call':
        return 'text-blue-600 bg-blue-100';
      case 'videochiamata':
        return 'text-purple-600 bg-purple-100';
      case 'email':
        return 'text-green-600 bg-green-100';
      case 'task':
        return 'text-orange-600 bg-orange-100';
      case 'appuntamento':
        return 'text-teal-600 bg-teal-100';
      case 'sopralluogo':
        return 'text-amber-600 bg-amber-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'overdue':
        return <XCircle className="h-4 w-4 text-red-600" />;
      default:
        return <Clock className="h-4 w-4 text-yellow-600" />;
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newActivity.title.trim()) {
      toast.error('Il titolo dell\'attività è richiesto');
      return;
    }

    const activityData = {
      ...newActivity,
      lead_id: lead.id,
      lead_name: lead.business_name,
      lead_stage: lead.lead_stage,
      due_date: newActivity.due_date || new Date().toISOString().split('T')[0],
      end_date: newActivity.end_date || undefined
    };

    onActivityCreate?.(activityData);
    setNewActivity({
      title: '',
      description: '',
      type: newActivity.type,
      due_date: '',
      end_date: '',
      status: 'pending'
    });
    setShowAddForm(false);
  };

  // Handle activity status toggle
  const handleStatusToggle = (activity: SalesActivity) => {
    const newStatus = activity.status === 'completed' ? 'pending' : 'completed';
    onActivityUpdate?.(activity.id, { 
      status: newStatus,
      completed_at: newStatus === 'completed' ? new Date().toISOString() : undefined
    });
  };

  // Sort activities by due date
  const sortedActivities = [...activities].sort((a, b) => {
    if (a.status === 'completed' && b.status !== 'completed') return 1;
    if (a.status !== 'completed' && b.status === 'completed') return -1;
    return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          Attività {leadLabel.del}{leadLabel.singular}
        </h3>
        {canCreate && (
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-1" />
            Nuova Attività
          </button>
        )}
      </div>

      {/* Add Activity Form */}
      {showAddForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Titolo *
                </label>
                <input
                  type="text"
                  required
                  className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={newActivity.title}
                  onChange={(e) => setNewActivity({ ...newActivity, title: e.target.value })}
                  placeholder="Es. Chiamata di follow-up"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={newActivity.type}
                  onChange={(e) => {
                    const newType = e.target.value;
                    setNewActivity({ ...newActivity, type: newType as any });
                  }}
                >
                  <option value="task">Task</option>
                  <option value="call">Chiamata</option>
                  <option value="videochiamata">Videochiamata</option>
                  <option value="appuntamento">Appuntamento fisico</option>
                  <option value="sopralluogo">Sopralluogo</option>
                  <option value="email">Email</option>
                  <option value="other">Altro</option>
                  {activeCustomTypes.length > 0 && (
                    <>
                      <option disabled>──── Tipi personalizzati ────</option>
                      {activeCustomTypes.map(customType => (
                        <option key={customType.slug} value={customType.slug}>
                          {customType.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrizione
              </label>
              <textarea
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={newActivity.description}
                onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })}
                placeholder="Dettagli dell'attività..."
              />
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data/ora inizio
                </label>
                <input
                  type="datetime-local"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={newActivity.due_date}
                  onChange={(e) => {
                    const newDueDate = e.target.value;
                    setNewActivity({
                      ...newActivity,
                      due_date: newDueDate,
                      end_date: calculateEndDate(newDueDate)
                    });
                  }}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data/ora fine
                </label>
                <input
                  type="datetime-local"
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={newActivity.end_date}
                  onChange={(e) => setNewActivity({ ...newActivity, end_date: e.target.value })}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Annulla
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-[#0f6cbd] border border-transparent rounded-lg hover:bg-[#0a5ba8] focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                Crea Attività
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Activities List */}
      <div className="space-y-3">
        {sortedActivities.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <MessageCircle className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p>Nessuna attività ancora creata per {leadLabel.questo} {leadLabel.singularLower}</p>
            {canCreate && (
              <button
                onClick={() => setShowAddForm(true)}
                className="mt-2 text-blue-600 hover:text-blue-700 text-sm font-medium"
              >
                Crea la prima attività
              </button>
            )}
          </div>
        ) : (
          sortedActivities.map((activity) => (
            <div
              key={activity.id}
              className={`bg-white border border-gray-200 rounded-lg p-4 ${
                activity.status === 'completed' ? 'opacity-75' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  {/* Activity Icon */}
                  <div className={`p-2 rounded-lg ${getActivityColor(activity.type)}`}>
                    {getActivityIcon(activity.type)}
                  </div>
                  
                  {/* Activity Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className={`text-sm font-medium ${
                        activity.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'
                      }`}>
                        {activity.title}
                      </h4>
                      {getStatusIcon(activity.status)}
                    </div>
                    
                    {activity.description && (
                      <p className="text-sm text-gray-600 mb-2">
                        {activity.description}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        <span>
                          {new Date(activity.due_date).toLocaleString('it-IT', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                          {activity.end_date && (
                            <> - {new Date(activity.end_date).toLocaleString('it-IT', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}</>
                          )}
                        </span>
                      </div>
                      
                      {activity.assignee && (
                        <div className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          <span>
                            {activity.assignee.full_name || activity.assignee.email}
                            {activity.user_id !== activity.assigned_to && activity.users?.full_name && (
                              <span className="text-gray-400"> (da {activity.users.full_name})</span>
                            )}
                          </span>
                        </div>
                      )}
                      
                      {activity.completed_at && (
                        <div className="flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          <span>
                            Completato: {new Date(activity.completed_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1">
                  {canUpdate && (
                    <button
                      onClick={() => handleStatusToggle(activity)}
                      className={`p-1 rounded-md ${
                        activity.status === 'completed'
                          ? 'text-yellow-600 hover:text-yellow-700 hover:bg-yellow-50'
                          : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                      }`}
                      title={activity.status === 'completed' ? 'Segna come da fare' : 'Segna come completato'}
                    >
                      {activity.status === 'completed' ? (
                        <XCircle className="h-4 w-4" />
                      ) : (
                        <CheckCircle className="h-4 w-4" />
                      )}
                    </button>
                  )}
                  
                  {(canUpdate || canDelete) && (
                    <button
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-md"
                      title="Altre opzioni"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}